<template>
  <div class="wrap relative" :style="'width: ' + map_width + '; height:' + map_height">
    <div id="mapImageContainer" :style="'width: ' + map_width + '; height:' + map_height" v-if="map_image_data">
      <img :src="map_image_data" alt="" />
    </div>

    <div v-else>
      <div id="mapContainer" ref="mapContainer" :style="'width: ' + map_width + '; height:' + map_height"></div>
      <div class="absolute zoom-wrap">
        <ZoomMap @scale-map="scaleMap" />
      </div>
    </div>
  </div>
</template>

<script>
import ZoomMap from './ZoomMap.vue';
import structuredClone from '@ungap/structured-clone';
import { UK_FALLBACK_CENTER } from '@/services/userLocation';
import {
  buildExclusionBubbleContent,
  createStopMarkerIcon,
  getExclusionMarkerPosition,
  lineStringFromExclusionItem,
} from '@/services/roadExclusionMap';
import {
  DRIVEN_ROUTE_STROKE,
  OVERLAY_ROUTE_LINE_WIDTH,
  PLANNED_ROUTE_LINE_WIDTH,
  ROUTE_SECTION_COLORS,
} from '@/constants/route-map-overlay';

const dashLineStyle = {
  lineDash: [1, 4],
  strokeColor: "white",
  lineWidth: 5,
  lineDashImage: H.map.SpatialStyle.DashImage.ARROW,
};

// LW-10: inactive route polyline on map
// Revert options (replace INACTIVE_ROUTE_STROKE value):
//   legacy gray:           'rgba(128, 128, 128, 0.5)'
//   desaturated blue trial: 'rgba(100, 140, 200, 0.70)'
const INACTIVE_ROUTE_STROKE = 'rgba(74, 74, 88, 0.85)';

const ROUTE_MARKER_WIDTH = 32;
const ROUTE_MARKER_HEIGHT = 32;
const ROUTE_MARKER_VIEWBOX = '0 0 64 64';
const ROUTE_MARKER_TIP_X = 32;
const ROUTE_MARKER_TIP_Y = 52.789;
const ROUTE_MARKER_SCALE = ROUTE_MARKER_WIDTH / 64;
const ROUTE_MARKER_ANCHOR = {
  x: ROUTE_MARKER_TIP_X * ROUTE_MARKER_SCALE,
  y: ROUTE_MARKER_TIP_Y * ROUTE_MARKER_SCALE,
};
const ROUTE_MARKER_SVG_OPEN =
  `<svg version="1.0" xmlns="http://www.w3.org/2000/svg" width="${ROUTE_MARKER_WIDTH}" height="${ROUTE_MARKER_HEIGHT}" viewBox="${ROUTE_MARKER_VIEWBOX}" xml:space="preserve">`;
const ROUTE_MARKER_PIN_PATHS =
  '<path fill="#fff" d="m32 52.789-12-18C18.5 32 16 28.031 16 24c0-8.836 7.164-16 16-16s16 7.164 16 16c0 4.031-2.055 8-4 10.789z"/><path d="M32 0C18.746 0 8 10.746 8 24c0 5.219 1.711 10.008 4.555 13.93.051.094.059.199.117.289l16 24a4 4 0 0 0 6.656 0l16-24c.059-.09.066-.195.117-.289C54.289 34.008 56 29.219 56 24 56 10.746 45.254 0 32 0m12 34.789-12 18-12-18C18.5 32 16 28.031 16 24c0-8.836 7.164-16 16-16s16 7.164 16 16c0 4.031-2.055 8-4 10.789"/>';

const ROUTE_START_MARKER_SVG =
  `${ROUTE_MARKER_SVG_OPEN}${ROUTE_MARKER_PIN_PATHS}<path d="m32 15-11 9h3v10h16V24h3Zm-3 19v-6h6v6Z"/></svg>`;

export default {
  name: 'HereMap',
  components: {
    ZoomMap,
  },
  props: {
    routeData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    is_edit: {
      type: Boolean,
      default: false,
    },
    reset_all: {
      type: Boolean,
      default: false,
    },
    multi_points_center: {
      type: Boolean,
      default: false,
    },
    selected_points: {
      type: Array,
      required: false,
    },
    is_plan: {
      type: Boolean,
      default: true,
    },
    map_width: {
      type: String,
      default: '100%',
    },
    selected_route_id: {
      type: [String, Number, null],
      default: null,
    },
    map_height: {
      type: String,
      default: '100vh',
    },
    zoom_default: {
      type: Number,
      default: 6.2,
    },
    initialCenter: {
      type: Object,
      default: null,
    },
    start_printing: {
      type: Boolean,
      default: false,
    },
    rote_replan: {
      type: Boolean,
      default: false,
    },
    stored_tmp_marker: {
      type: Array,
      default: () => [],
    },
    exclusionOverlays: {
      type: Array,
      default: () => [],
    },
    routeVersionMode: {
      type: Boolean,
      default: false,
    },
    selectedVersionIndex: {
      type: Number,
      default: 0,
    },
    plannedBaseOverlay: {
      type: Boolean,
      default: false,
    },
    planMapPickEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      map: null,
      mapUi: null,
      geocoder: null,
      planDraftMarker: null,
      planContextMenuHandler: null,
      exclusionGroup: null,
      exclusionActiveBubble: null,
      stopMarkerIcon: null,
      map_image_data: '',
      behavior: null,
      generated_route: false,
      stop_dragging: false,
      cloned_points: null,
      tmp_marker: [],
      tmp_markers_item_origin_geometry: null,
      mapDragListenersBound: false,
      selectedRouteIndex: 0,
      colors: [...ROUTE_SECTION_COLORS],
    };
  },
  methods: {
    ensureExclusionGroup() {
      if (!this.map) {
        return;
      }
      const onMap = this.exclusionGroup
        && this.map.getObjects().includes(this.exclusionGroup);
      if (!onMap) {
        this.exclusionGroup = new H.map.Group();
        this.map.addObject(this.exclusionGroup);
      }
    },
    async clearMapRouteObjects() {
      if (!this.map) {
        return;
      }
      const objects = this.map.getObjects().filter(
        (obj) => obj !== this.exclusionGroup,
      );
      if (objects.length) {
        await this.map.removeObjects(objects);
      }
    },
    closeExclusionBubble() {
      if (this.exclusionActiveBubble && this.mapUi) {
        this.mapUi.removeBubble(this.exclusionActiveBubble);
        this.exclusionActiveBubble = null;
      }
    },
    openExclusionBubble(item, position) {
      if (!this.mapUi || !position) {
        return;
      }
      this.closeExclusionBubble();
      this.exclusionActiveBubble = new H.ui.InfoBubble(position, {
        content: buildExclusionBubbleContent(item),
      });
      this.mapUi.addBubble(this.exclusionActiveBubble);
    },
    renderExclusionOverlays(items) {
      if (!this.map) {
        return;
      }
      this.closeExclusionBubble();
      this.ensureExclusionGroup();
      this.exclusionGroup.removeAll();
      if (!items?.length) {
        return;
      }
      if (!this.stopMarkerIcon) {
        this.stopMarkerIcon = createStopMarkerIcon();
      }
      items.forEach((item) => {
        const linestring = lineStringFromExclusionItem(item);
        if (linestring) {
          const polyline = new H.map.Polyline(linestring, {
            style: { strokeColor: '#FF0000', lineWidth: 8 },
          });
          polyline.setData({ role: 'road-exclusion-line', segmentId: item.segment_id });
          this.exclusionGroup.addObject(polyline);
        }
        const position = getExclusionMarkerPosition(item);
        if (position) {
          const marker = new H.map.Marker(position, {
            icon: this.stopMarkerIcon,
            data: { role: 'road-exclusion-pin', item },
          });
          marker.addEventListener('tap', (evt) => {
            evt.stopPropagation();
            this.openExclusionBubble(item, position);
          });
          this.exclusionGroup.addObject(marker);
        }
      });
    },
    coordsMatch(a, b, eps = 1e-5) {
      if (!a || !b) return false;
      return (
        Math.abs(a.lat - b.lat) < eps && Math.abs(a.lng - b.lng) < eps
      );
    },
    setMarkerMeta(marker, meta) {
      if (marker && typeof marker.setData === 'function') {
        marker.setData(meta);
      } else if (marker) {
        marker._planMeta = meta;
      }
    },
    getMarkerMeta(marker) {
      if (!marker) return {};
      if (typeof marker.getData === 'function') {
        return marker.getData() || {};
      }
      return marker._planMeta || {};
    },
    scaleMap(zoomIn) {
      const currentZoom = this.map.getZoom();
      const newZoom = zoomIn ? currentZoom + 1 : currentZoom - 1;
      this.map.setZoom(newZoom);
    },
    async displayRoute() {
      if (!this.routeData.routes) {
        return;
      }
      if (this.multi_points_center) {
        return await this.renderClonedMarkers();
      }

      this.generated_route = true;
      const route = this.routeData.routes[0];

      await this.clearMapRouteObjects();
      let latitudes = [];
      let longitudes = [];

      route.sections.forEach((section, index) => {
        const linestring = H.geo.LineString.fromFlexiblePolyline(
          section.polyline,
        );
        const routeLine = new H.map.Polyline(linestring, {
          style: {
            strokeColor: this.colors[index % this.colors.length],
            lineWidth: 3,
            fillColor: 'white',
          }
        });
        const dashLine = new H.map.Polyline(linestring, {
          style: dashLineStyle
        });

        this.map.addObject(routeLine);
        this.map.addObject(dashLine);
        linestring.getLatLngAltArray().forEach((value, index) => {
          if (index % 3 === 0) latitudes.push(value);
          if (index % 3 === 1) longitudes.push(value);
        });
      });
      await this.renderMarkers();
      this.setMarkersDraggableForView();
      this.map.getObjects().forEach((obj) => {
        if (obj instanceof H.map.Marker) {
          const point = obj.getGeometry();
          latitudes.push(point.lat);
          longitudes.push(point.lng);
        }
      });
      if (latitudes.length > 0 && longitudes.length > 0) {
        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);
        const latDelta = (maxLat - minLat) * 0.1;
        const lngDelta = (maxLng - minLng) * 0.58;
        const paddedMinLat = minLat - latDelta;
        const paddedMaxLat = maxLat + latDelta;
        const paddedMinLng = minLng - lngDelta;
        const paddedMaxLng = maxLng + lngDelta;
        const paddedBoundingRect = new H.geo.Rect(
          paddedMinLat,
          paddedMinLng,
          paddedMaxLat,
          paddedMaxLng,
        );
        this.map
          .getViewModel()
          .setLookAtData({ bounds: paddedBoundingRect }, false);
      }
    },

    async renderClonedMarkers() {
      await this.clearMapRouteObjects();

      let points = this.selected_points;
      points = this.routeData.routes[0].sections.map((section) => {
        return {
          lat: section.departure.place.originalLocation.lat,
          lng: section.departure.place.originalLocation.lng,
        };
      });
      points.push({
        lat: this.routeData.routes[0].sections[
          this.routeData.routes[0].sections.length - 1
        ].arrival.place.originalLocation.lat,
        lng: this.routeData.routes[0].sections[
          this.routeData.routes[0].sections.length - 1
        ].arrival.place.originalLocation.lng,
      });
      const validPoints = this.filterValidWaypointPoints(points);
      const totalPoints = validPoints.length;
      validPoints.forEach((point, sequentialIndex) => {
        const sidebarIndex = this.getWaypointSidebarIndex(
          point,
          sequentialIndex,
        );
        const role = this.getWaypointRole(
          point,
          sidebarIndex,
          sequentialIndex,
          totalPoints,
        );
        const marker = new H.map.Marker(
          {
            lat: point.lat,
            lng: point.lng,
          },
          {
            icon: new H.map.Icon(
              this.generateMarkerSVG(validPoints, sequentialIndex, role),
            ),
          },
        );
        marker.draggable = true;
        this.setMarkerMeta(marker, {
          role: 'waypoint',
          waypointIndex: sequentialIndex,
        });
        this.map.addObject(marker);
      });

      await this.setupDragging();
      let latitudes = [];
      let longitudes = [];
      this.map.getObjects().forEach((obj) => {
        if (obj instanceof H.map.Marker) {
          const point = obj.getGeometry();
          latitudes.push(point.lat);
          longitudes.push(point.lng);
        }
      });
      if (latitudes.length > 0 && longitudes.length > 0) {
        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);
        const latDelta = (maxLat - minLat) * 0.1;
        const lngDelta = (maxLng - minLng) * 0.58;
        const paddedMinLat = minLat - latDelta;
        const paddedMaxLat = maxLat + latDelta;
        const paddedMinLng = minLng - lngDelta;
        const paddedMaxLng = maxLng + lngDelta;
        const paddedBoundingRect = new H.geo.Rect(
          paddedMinLat,
          paddedMinLng,
          paddedMaxLat,
          paddedMaxLng,
        );
        this.map
          .getViewModel()
          .setLookAtData({ bounds: paddedBoundingRect }, false);
      }
    },

    async renderMarkers() {
      let points = this.selected_points;
      if (!this.is_plan) {
        points = this.buildViewRouteMarkerPoints(
          this.routeData.routes[0].sections,
        );
      }
      const validPoints = this.filterValidWaypointPoints(points);
      const totalPoints = validPoints.length;
      validPoints.forEach((point, sequentialIndex) => {
        const sidebarIndex = this.getWaypointSidebarIndex(
          point,
          sequentialIndex,
        );
        const role = this.getWaypointRole(
          point,
          sidebarIndex,
          sequentialIndex,
          totalPoints,
        );
        const svg = this.generateMarkerSVG(validPoints, sequentialIndex, role);
        const icon = this.is_plan
          ? new H.map.Icon(svg)
          : this.createRouteMarkerIcon(svg);
        const marker = new H.map.Marker(
          {
            lat: point.lat,
            lng: point.lng,
          },
          { icon },
        );
        marker.draggable = false;
        this.setMarkerMeta(marker, {
          role: 'waypoint',
          waypointIndex: sequentialIndex,
        });
        this.map.addObject(marker);
      });

      if (this.stored_tmp_marker?.length) {
        this.tmp_marker = this.stored_tmp_marker;
      }

      // Render passthrough markers once (not once per location marker)
      if (this.tmp_marker?.length) {
        this.tmp_marker.forEach((tmpPoint, tmpIndex) => {
          const isEntryOrExit =
            tmpPoint.type === 'entry' || tmpPoint.type === 'exit';

          let icon;
          if (isEntryOrExit) {

            icon = new H.map.Icon(
              this.generateEntryExitMarkerSVG(tmpPoint.type),
            );
          } else {
            // Manual passthroughs: keep circular blue marker
            icon = new H.map.Icon(this.circleSvg(), {
              anchor: { x: 9, y: 9 },
            });
          }

          const marker = new H.map.Marker(
            { lat: tmpPoint.coordinates.lat, lng: tmpPoint.coordinates.lng },
            { icon },
          );
          marker.draggable = true;
          this.setMarkerMeta(marker, {
            role: 'passthrough',
            passthroughIndex: tmpIndex,
          });
          marker.addEventListener('tap', () => {
            this.onTMPMarkerClick(marker);
          });
          this.map.addObject(marker);
        });
      }
    },
    async onTMPMarkerClick(marker) {
      this.tmp_marker = this.tmp_marker.filter((point) => {
        return (
          point.coordinates.lat !== marker.getGeometry().lat ||
          point.coordinates.lng !== marker.getGeometry().lng
        );
      });
      await this.map.removeObject(marker);
      await this.$emit('changed_way', this.tmp_marker);
    },
    async initMap() {
      const platform = new H.service.Platform({
        apikey: process.env.VUE_APP_HERE_API_KEY,
      });

      const defaultLayers = platform.createDefaultLayers({ pois: true, traffic: true });

      const mapCenter = this.initialCenter?.lat != null
        ? { lat: this.initialCenter.lat, lng: this.initialCenter.lng }
        : UK_FALLBACK_CENTER;

      const map = new H.Map(
        this.$refs.mapContainer,
        defaultLayers.vector.normal.logistics,
        {
          zoom: this.zoom_default,
          center: mapCenter,
          engineType: H.Map.EngineType.HARP
        });

      this.map = map;
      this.map.addLayer(defaultLayers.vector.traffic.map);
      // this.map.addLayer(defaultLayers.vector.traffic.logistics);
      const mapEvents = new H.mapevents.MapEvents(this.map);
      this.behavior = await new H.mapevents.Behavior(mapEvents);

      const viewport = this.map.getViewPort();

      function zoomToCursor(event, zoomChange) {
        event.stopImmediatePropagation();
        event.preventDefault();

        const rect = viewport.element.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        // Capture the geo point under the cursor BEFORE zooming
        const geoUnderCursor = map.screenToGeo(mouseX, mouseY);

        map.setZoom(map.getZoom() + zoomChange, false);

        // After zoom, find where that geo point ended up on screen
        const newScreen = map.geoToScreen(geoUnderCursor);

        // Calculate how much it drifted from the original mouse position
        const driftX = newScreen.x - mouseX;
        const driftY = newScreen.y - mouseY;

        // Shift the center to compensate for the drift
        const currentCenter = map.screenToGeo(
          rect.width / 2 + driftX,
          rect.height / 2 + driftY
        );

        map.setCenter(currentCenter, false);
      }


      // await H.ui.UI.createDefault(map, defaultLayers);
      const ui = H.ui.UI.createDefault(this.map, defaultLayers);
      this.mapUi = ui;
      ui.removeControl('mapsettings');


      // Re-add with your custom options
      const customMapSettings = new H.ui.MapSettingsControl({
        baseLayers: [
          {
            label: 'Standard', layer: [
              defaultLayers.vector.normal.logistics
            ]
          },
          {
            label: 'Satellite', layer: [
              defaultLayers.raster.satellite.map,
            ]
          }
        ],
      });

      viewport.element.addEventListener('wheel', (e) => {
        const delta = e.deltaY < 0 ? 0.5 : -0.5;
        zoomToCursor(e, delta);
      }, { capture: true, passive: false });

      viewport.element.addEventListener('dblclick', (e) => {
        zoomToCursor(e, 2);
      }, { capture: true });

      ui.addControl('mapsettings', customMapSettings);

      // this.map.addLayer(defaultLayers.vector.traffic.logistics);
      const style = this.map.getBaseLayer().getProvider().getStyle();

      const styleChangeHandler = () => {
        if (style.getState() === H.map.render.Style.State.READY) {
          style.removeEventListener('change', styleChangeHandler, false);

          const enabledFeatures = [
            { feature: 'vehicle restrictions', mode: 'active & inactive' },
            { feature: 'congestion zones', mode: 'all' },
            { feature: 'environmental zones', mode: 'all' }
          ];
          style.setEnabledFeatures(enabledFeatures);
        }
      };
      style.addEventListener('change', styleChangeHandler);

      // this.map.getBaseLayer().getProvider().setVehicleSpecs(undefined);

      window.addEventListener("resize", () => viewport.resize());
      this.ensureExclusionGroup();
      this.renderExclusionOverlays(this.exclusionOverlays);
      const service = platform.getSearchService();
      this.geocoder = service;
      if (this.is_plan) {
        this.bindPlanContextMenuIfEnabled();
      }
      if (!this.is_plan) {
        this.setupStoredRoute();
      }
    },
    bindPlanContextMenuIfEnabled() {
      if (!this.map || !this.is_plan) {
        return;
      }
      const viewport = this.map.getViewPort();
      if (this.planContextMenuHandler) {
        viewport.element.removeEventListener(
          'contextmenu',
          this.planContextMenuHandler,
        );
        this.planContextMenuHandler = null;
      }
      if (!this.planMapPickEnabled) {
        return;
      }
      this.planContextMenuHandler = (evt) => {
        evt.preventDefault();
        const rect = viewport.element.getBoundingClientRect();
        const x = evt.clientX - rect.left;
        const y = evt.clientY - rect.top;
        const coord = this.map.screenToGeo(x, y);
        this.showPlanDraftPin(coord.lat, coord.lng);
        this.$emit('plan-map-context-open', {
          lat: coord.lat,
          lng: coord.lng,
          clientX: evt.clientX,
          clientY: evt.clientY,
          address: 'Loading address...',
        });
        if (!this.geocoder) {
          return;
        }
        this.geocoder.reverseGeocode(
          { at: `${coord.lat},${coord.lng}` },
          (result) => {
            const address =
              result.items?.length > 0
                ? result.items[0].address.label
                : `${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`;
            this.$emit('plan-map-context-address', { address });
          },
          () => {
            this.$emit('plan-map-context-address', {
              address: `${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`,
            });
          },
        );
      };
      viewport.element.addEventListener('contextmenu', this.planContextMenuHandler);
    },
    showPlanDraftPin(lat, lng) {
      this.clearPlanDraftPin();
      if (!this.map) {
        return;
      }
      const iconSvg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42"><path fill="#1976d2" stroke="#fff" stroke-width="2" d="M15,40 C15,40 2,24 2,15 C2,8 8,2 15,2 C22,2 28,8 28,15 C28,24 15,40 15,40 Z" /></svg>';
      const icon = new H.map.Icon(
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(iconSvg),
        { anchor: { x: 15, y: 40 } },
      );
      const marker = new H.map.Marker({ lat, lng }, { icon });
      this.setMarkerMeta(marker, { role: 'draft' });
      this.map.addObject(marker);
      this.planDraftMarker = marker;
    },
    clearPlanDraftPin() {
      if (this.planDraftMarker && this.map) {
        this.map.removeObject(this.planDraftMarker);
        this.planDraftMarker = null;
      }
    },
    reverseGeocodeAt(lat, lng) {
      const fallback = `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
      if (!this.geocoder) {
        return Promise.resolve(fallback);
      }
      return new Promise((resolve) => {
        this.geocoder.reverseGeocode(
          { at: `${lat},${lng}` },
          (result) => {
            const address =
              result.items?.length > 0
                ? result.items[0].address.label
                : fallback;
            resolve(address);
          },
          () => resolve(fallback),
        );
      });
    },
    async syncWaypointMarkersFromPoints(points) {
      if (!this.map) {
        return;
      }
      const toRemove = [];
      this.map.getObjects().forEach((obj) => {
        if (
          obj instanceof H.map.Marker &&
          this.getMarkerMeta(obj).role === 'waypoint'
        ) {
          toRemove.push(obj);
        }
      });
      if (toRemove.length) {
        await this.map.removeObjects(toRemove);
      }
      const validPoints = this.filterValidWaypointPoints(points);
      const totalPoints = validPoints.length;
      validPoints.forEach((point, sequentialIndex) => {
        const sidebarIndex = this.getWaypointSidebarIndex(
          point,
          sequentialIndex,
        );
        const role = this.getWaypointRole(
          point,
          sidebarIndex,
          sequentialIndex,
          totalPoints,
        );
        const svg = this.generateMarkerSVG(validPoints, sequentialIndex, role);
        const icon = new H.map.Icon(svg);
        const marker = new H.map.Marker(
          { lat: point.lat, lng: point.lng },
          { icon },
        );
        marker.draggable = true;
        this.setMarkerMeta(marker, {
          role: 'waypoint',
          waypointIndex: sequentialIndex,
        });
        this.map.addObject(marker);
      });
      await this.setupDragging();
    },
    async setupStoredRoute() {
      if (!this.routeData) {
        return;
      }
      if (this.routeVersionMode) {
        await this.displayRouteVersions(this.selectedVersionIndex || 0);
        return;
      }
      await this.displayRoute();
    },
    async displayRouteVersions(selectedIndex = 0) {
      if (!this.routeData?.routes?.length) {
        return;
      }

      if (this.plannedBaseOverlay) {
        await this.displayPlannedBaseOverlay(selectedIndex);
        return;
      }

      this.generated_route = true;
      await this.clearMapRouteObjects();
      this.routePolylines = [];

      let latitudes = [];
      let longitudes = [];

      const appendRoutePolylines = (route, routeIndex) => {
        const routePolylines = [];
        const isSelectedRoute = routeIndex === selectedIndex;

        route.sections.forEach((section, sectionIndex) => {
          const linestring = H.geo.LineString.fromFlexiblePolyline(
            section.polyline,
          );
          const sectionColor = this.colors[sectionIndex % this.colors.length];
          const strokeColor = isSelectedRoute
            ? sectionColor
            : INACTIVE_ROUTE_STROKE;

          const routeLine = new H.map.Polyline(linestring, {
            style: {
              strokeColor,
              lineWidth: 3,
            },
          });
          const dashLine = new H.map.Polyline(routeLine.getGeometry(), {
            style: dashLineStyle,
          });

          routePolylines.push(routeLine);
          this.map.addObject(routeLine);
          this.map.addObject(dashLine);

          if (isSelectedRoute) {
            linestring.getLatLngAltArray().forEach((value, index) => {
              if (index % 3 === 0) latitudes.push(value);
              if (index % 3 === 1) longitudes.push(value);
            });
          }
        });

        this.routePolylines[routeIndex] = routePolylines;
      };

      this.routeData.routes.forEach((route, routeIndex) => {
        if (routeIndex !== selectedIndex) {
          appendRoutePolylines(route, routeIndex);
        }
      });

      const selectedRoute = this.routeData.routes[selectedIndex];
      if (selectedRoute) {
        appendRoutePolylines(selectedRoute, selectedIndex);
      }

      if (latitudes.length > 0 && longitudes.length > 0) {
        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);
        const latDelta = (maxLat - minLat) * 0.1;
        const lngDelta = (maxLng - minLng) * 0.58;
        const paddedBoundingRect = new H.geo.Rect(
          minLat - latDelta,
          minLng - lngDelta,
          maxLat + latDelta,
          maxLng + lngDelta,
        );
        this.map
          .getViewModel()
          .setLookAtData({ bounds: paddedBoundingRect }, false);
      }
    },
    collectRouteBounds(route, latitudes, longitudes) {
      if (!route?.sections?.length) {
        return;
      }

      route.sections.forEach((section) => {
        const linestring = H.geo.LineString.fromFlexiblePolyline(
          section.polyline,
        );
        linestring.getLatLngAltArray().forEach((value, index) => {
          if (index % 3 === 0) latitudes.push(value);
          if (index % 3 === 1) longitudes.push(value);
        });
      });
    },
    fitMapToBounds(latitudes, longitudes) {
      if (latitudes.length === 0 || longitudes.length === 0) {
        return;
      }

      const minLat = Math.min(...latitudes);
      const maxLat = Math.max(...latitudes);
      const minLng = Math.min(...longitudes);
      const maxLng = Math.max(...longitudes);
      const latDelta = (maxLat - minLat) * 0.1;
      const lngDelta = (maxLng - minLng) * 0.58;
      const paddedBoundingRect = new H.geo.Rect(
        minLat - latDelta,
        minLng - lngDelta,
        maxLat + latDelta,
        maxLng + lngDelta,
      );
      this.map
        .getViewModel()
        .setLookAtData({ bounds: paddedBoundingRect }, false);
    },
    async displayPlannedBaseOverlay(selectedIndex = 1) {
      if (!this.routeData?.routes?.length) {
        return;
      }

      this.generated_route = true;
      await this.clearMapRouteObjects();
      this.routePolylines = [];

      const latitudes = [];
      const longitudes = [];
      const plannedRoute = this.routeData.routes[0];
      const overlayRoute = this.routeData.routes[selectedIndex];

      if (plannedRoute) {
        const plannedPolylines = [];
        plannedRoute.sections.forEach((section, sectionIndex) => {
          const linestring = H.geo.LineString.fromFlexiblePolyline(
            section.polyline,
          );
          const sectionColor =
            ROUTE_SECTION_COLORS[sectionIndex % ROUTE_SECTION_COLORS.length];
          const routeLine = new H.map.Polyline(linestring, {
            style: {
              strokeColor: sectionColor,
              lineWidth: PLANNED_ROUTE_LINE_WIDTH,
            },
          });
          const dashLine = new H.map.Polyline(routeLine.getGeometry(), {
            style: dashLineStyle,
          });

          plannedPolylines.push(routeLine);
          this.map.addObject(routeLine);
          this.map.addObject(dashLine);
        });
        this.routePolylines[0] = plannedPolylines;
        this.collectRouteBounds(plannedRoute, latitudes, longitudes);
      }

      if (overlayRoute && selectedIndex > 0) {
        const overlayPolylines = [];
        overlayRoute.sections.forEach((section) => {
          const linestring = H.geo.LineString.fromFlexiblePolyline(
            section.polyline,
          );
          const routeLine = new H.map.Polyline(linestring, {
            style: {
              strokeColor: DRIVEN_ROUTE_STROKE,
              lineWidth: OVERLAY_ROUTE_LINE_WIDTH,
            },
          });
          const dashLine = new H.map.Polyline(routeLine.getGeometry(), {
            style: dashLineStyle,
          });

          overlayPolylines.push(routeLine);
          this.map.addObject(routeLine);
          this.map.addObject(dashLine);
        });
        this.routePolylines[selectedIndex] = overlayPolylines;
        this.collectRouteBounds(overlayRoute, latitudes, longitudes);
      }

      await this.renderMarkers();
      this.setMarkersDraggableForView();
      this.map.getObjects().forEach((obj) => {
        if (obj instanceof H.map.Marker) {
          const point = obj.getGeometry();
          latitudes.push(point.lat);
          longitudes.push(point.lng);
        }
      });
      this.fitMapToBounds(latitudes, longitudes);
    },
    async displayAlternativeRoutes() {
      if (
        !this.routeData.routes ||
        (this.$route.name === 'HereRePlan' && !this.rote_replan)
      ) {
        return;
      }

      this.generated_route = true;
      await this.clearMapRouteObjects();
      let latitudes = [];
      let longitudes = [];

      this.routePolylines = [];

      this.routeData.routes.forEach((route, routeIndex) => {
        const routePolylines = [];

        route.sections.forEach((section, sectionIndex) => {
          const linestring = H.geo.LineString.fromFlexiblePolyline(
            section.polyline,
          );

          const sectionColor = this.colors[sectionIndex % this.colors.length];

          const isMainRoute = routeIndex === 0;
          const strokeColor = isMainRoute
            ? sectionColor
            : INACTIVE_ROUTE_STROKE;


          const routeLine = new H.map.Polyline(linestring, {
            style: {
              strokeColor,
              lineWidth: 3
            },
          });
          const dashLine = new H.map.Polyline(routeLine.getGeometry(), {
            style: dashLineStyle
          });
          routeLine.sectionIndex = sectionIndex;
          dashLine.sectionIndex = sectionIndex;

          routePolylines.push(routeLine);
          routeLine.draggable = true;
          dashLine.draggable = true;

          routeLine.addEventListener(
            'dragstart',
            this.onSegmentDragStart.bind(this),
          );
          dashLine.addEventListener(
            'dragstart',
            this.onSegmentDragStart.bind(this),
          );
          routeLine.addEventListener('drag', this.onSegmentDrag.bind(this));
          dashLine.addEventListener('drag', this.onSegmentDrag.bind(this));
          routeLine.addEventListener(
            'dragend',
            this.onSegmentDragEnd.bind(this),
          );
          dashLine.addEventListener(
            'dragend',
            this.onSegmentDragEnd.bind(this),
          );
          if (routeIndex > 0) {
            return;
          }

          this.map.addObject(routeLine);
          this.map.addObject(dashLine);
          linestring.getLatLngAltArray().forEach((value, index) => {
            if (index % 3 === 0) latitudes.push(value);
            if (index % 3 === 1) longitudes.push(value);
          });
        });
        this.routePolylines.push(routePolylines);
      });

      await this.renderMarkers();
      this.setMarkersDraggableForView();

      this.map.getObjects().forEach((obj) => {
        if (obj instanceof H.map.Marker) {
          const point = obj.getGeometry();
          latitudes.push(point.lat);
          longitudes.push(point.lng);
        }
      });

      if (latitudes.length > 0 && longitudes.length > 0) {
        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);
        const latDelta = (maxLat - minLat) * 0.1;
        const lngDelta = (maxLng - minLng) * 0.58;
        const paddedBoundingRect = new H.geo.Rect(
          minLat - latDelta,
          minLng - lngDelta,
          maxLat + latDelta,
          maxLng + lngDelta,
        );
        this.map
          .getViewModel()
          .setLookAtData({ bounds: paddedBoundingRect }, false);
      }
    },

    onSegmentDragStart(evt) {
      this.behavior.disable();
      document.body.style.cursor = `url('data:image/svg+xml;base64,${btoa(
        this.circleSvg(),
      )}'), auto`;
      if (!evt?.target) {
        return;
      }
    },

    onSegmentDrag(evt) {
      if (!evt?.target) {
        return;
      }
      this.map.screenToGeo(
        evt.currentPointer.viewportX,
        evt.currentPointer.viewportY,
      );
    },

    onSegmentDragEnd(evt) {
      if (!evt?.target) {
        return;
      }
      document.body.style.cursor = 'default';
      const pointer = this.map.screenToGeo(
        evt.currentPointer.viewportX,
        evt.currentPointer.viewportY,
      );
      const sectionIndex = evt.target.sectionIndex;
      if (this.tmp_marker.length) {
        this.tmp_marker.push({
          sectionIndex,
          coordinates: { lat: pointer.lat, lng: pointer.lng },
          type: 'manual',
        });
      } else {
        this.tmp_marker.push({
          sectionIndex,
          coordinates: { lat: pointer.lat, lng: pointer.lng },
          type: 'manual',
        });
      }
      this.$emit('changed_way', this.tmp_marker);
      this.behavior.enable();
    },

    highlightSelectedRoute(routeIndex) {
      if (!this.routePolylines?.length) {
        return;
      }
      this.routePolylines.forEach((routePolylines) => {
        if (!routePolylines?.length) {
          return;
        }
        routePolylines.forEach((polyline) => {
          polyline.setStyle({
            strokeColor: INACTIVE_ROUTE_STROKE,
            lineWidth: 3
          });
        });
      });

      const selectedRoutePolylines = this.routePolylines[routeIndex];

      selectedRoutePolylines.forEach((polyline, sectionIndex) => {
        const sectionColor = this.colors[sectionIndex % this.colors.length];
        polyline.setStyle({
          strokeColor: sectionColor,
          lineWidth: 3,
        });

        const dashLine = new H.map.Polyline(polyline.getGeometry(), {
          style: dashLineStyle
        });

        this.map.addObject(dashLine);
        this.map.addObject(polyline);
      });

      this.selectedRouteIndex = routeIndex;

      this.$emit('selected_way', routeIndex);
    },

    async chooseRoute(route_id) {
      const routeIndex = this.routeData.routes.findIndex(
        (route) => route.id === route_id,
      );
      this.highlightSelectedRoute(routeIndex);
    },

    async renderAddedPoints(points, changed_point_index) {
      if (this.generated_route) {
        return;
      }
      await this.clearMapRouteObjects();

      const validPoints = this.filterValidWaypointPoints(points);
      const totalPoints = validPoints.length;
      validPoints.forEach((point, sequentialIndex) => {
        const sidebarIndex = this.getWaypointSidebarIndex(
          point,
          sequentialIndex,
        );
        const role = this.getWaypointRole(
          point,
          sidebarIndex,
          sequentialIndex,
          totalPoints,
        );
        const icon = new H.map.Icon(
          this.generateMarkerSVG(validPoints, sequentialIndex, role),
        );
        const marker = new H.map.Marker(
          { lat: point.lat, lng: point.lng },
          { icon },
        );

        marker.draggable = true;
        this.setMarkerMeta(marker, {
          role: 'waypoint',
          waypointIndex: sequentialIndex,
        });
        this.map.addObject(marker);
      });
      await this.setupDragging();

      if (this.$route.name !== 'HereRePlan') {
        await this.zoomPoints(changed_point_index);
      }
    },

    async zoomPoints(changed_point_index = 0) {
      let latitudes = [];
      let longitudes = [];
      this.map.getObjects().forEach((obj) => {
        if (obj instanceof H.map.Marker) {
          const point = obj.getGeometry();
          latitudes.push(point.lat);
          longitudes.push(point.lng);
        }
      });
      let paddedBoundingRect = null;
      if (latitudes[changed_point_index]) {
        paddedBoundingRect = new H.geo.Point(
          latitudes[changed_point_index],
          longitudes[changed_point_index],
        );
      } else {
        paddedBoundingRect = new H.geo.Point(latitudes[0], longitudes[0]);
      }
      await this.map
        .getViewModel()
        .setLookAtData({ bounds: paddedBoundingRect, zoom: 14 }, false);
    },

    setMarkersDraggableForView() {
      if (!this.map) {
        return;
      }
      if (this.is_edit) {
        if (this.stored_tmp_marker?.length) {
          this.tmp_marker = this.stored_tmp_marker;
        }
        this.map.getObjects().forEach((obj) => {
          if (obj instanceof H.map.Marker) {
            obj.draggable = true;
          }
        });
        this.generated_route = false;
        this.setupDragging();
        return;
      }
      this.map.getObjects().forEach((obj) => {
        if (obj instanceof H.map.Marker) {
          obj.draggable = false;
        }
      });
    },
    onMapDragStart(ev) {
      const target = ev.target;
      if (target instanceof H.map.Marker) {
        const pointer = ev.currentPointer;
        this.tmp_markers_item_origin_geometry = target.getGeometry();
        const targetPosition = this.map.geoToScreen(target.getGeometry());
        target.offset = new H.math.Point(
          pointer.viewportX - targetPosition.x,
          pointer.viewportY - targetPosition.y,
        );
        this.behavior.disable(H.mapevents.Behavior.Feature.PANNING);
      }
    },
    onMapDrag(ev) {
      const target = ev.target;
      const pointer = ev.currentPointer;
      if (target instanceof H.map.Marker) {
        target.setGeometry(
          this.map.screenToGeo(
            pointer.viewportX - target.offset.x,
            pointer.viewportY - target.offset.y,
          ),
        );
      }
    },
    onMapDragEnd(ev) {
      const target = ev.target;
      if (!(target instanceof H.map.Marker)) {
        return;
      }
      this.behavior.enable(H.mapevents.Behavior.Feature.PANNING);
      const origin = this.tmp_markers_item_origin_geometry;
      const newPosition = target.getGeometry();
      const meta = this.getMarkerMeta(target);

      if (meta.role === 'passthrough' && this.tmp_marker?.length) {
        const passthroughIndex =
          typeof meta.passthroughIndex === 'number'
            ? meta.passthroughIndex
            : this.tmp_marker.findIndex((point) =>
                this.coordsMatch(point.coordinates, origin),
              );

        if (passthroughIndex >= 0) {
          this.tmp_marker[passthroughIndex] = {
            ...this.tmp_marker[passthroughIndex],
            coordinates: {
              lat: newPosition.lat,
              lng: newPosition.lng,
            },
          };
        }
        this.$emit('changed_way', [...this.tmp_marker]);
        this.tmp_markers_item_origin_geometry = newPosition;
        return;
      }

      if (meta.role === 'waypoint') {
        const mapMarkers = this.map
          .getObjects()
          .filter((obj) => obj instanceof H.map.Marker);
        const waypointMarkers = mapMarkers
          .filter((marker) => this.getMarkerMeta(marker).role === 'waypoint')
          .sort(
            (a, b) =>
              (this.getMarkerMeta(a).waypointIndex || 0) -
              (this.getMarkerMeta(b).waypointIndex || 0),
          );
        const updatedPoints = waypointMarkers.map((marker) => {
          const position = marker.getGeometry();
          return { lat: position.lat, lng: position.lng };
        });
        this.$emit('update_points', updatedPoints);
        return;
      }

      const mapMarkers = this.map
        .getObjects()
        .filter((obj) => obj instanceof H.map.Marker);
      const waypointCount = this.selected_points?.length || 0;
      if (waypointCount && mapMarkers.length > waypointCount) {
        const updatedPoints = mapMarkers.slice(0, waypointCount).map((marker) => {
          const position = marker.getGeometry();
          return { lat: position.lat, lng: position.lng };
        });
        this.$emit('update_points', updatedPoints);
        return;
      }

      const updatedPoints = mapMarkers.map((marker) => {
        const position = marker.getGeometry();
        return { lat: position.lat, lng: position.lng };
      });
      this.$emit('update_points', updatedPoints);
    },
    setupDragging() {
      if (!this.map || !this.behavior) {
        return;
      }
      if (this.mapDragListenersBound) {
        return;
      }
      this.map.addEventListener('dragstart', this.onMapDragStart, false);
      this.map.addEventListener('drag', this.onMapDrag, false);
      this.map.addEventListener('dragend', this.onMapDragEnd, false);
      this.mapDragListenersBound = true;
    },
    isValidMarkerPoint(point) {
      return (
        point &&
        typeof point.lat === 'number' &&
        typeof point.lng === 'number' &&
        !isNaN(point.lat) &&
        !isNaN(point.lng) &&
        !(point.lat === 0 && point.lng === 0)
      );
    },
    filterValidWaypointPoints(points) {
      if (!Array.isArray(points)) {
        return [];
      }
      return points.filter((point) => this.isValidMarkerPoint(point));
    },
    getWaypointSidebarIndex(point, sequentialIndex) {
      if (point && typeof point.sidebarIndex === 'number') {
        return point.sidebarIndex;
      }
      return sequentialIndex;
    },
    getWaypointRole(point, sidebarIndex, sequentialIndex, totalPoints) {
      const role = point && point.role;
      if (role === 'start' || role === 'destination' || role === 'via') {
        return role;
      }
      if (totalPoints === 1 && sidebarIndex > 0) {
        return 'destination';
      }
      if (sidebarIndex === 0) {
        return 'start';
      }
      if (sequentialIndex === totalPoints - 1) {
        return 'destination';
      }
      return 'via';
    },
    getNonStartStopNumber(validPoints, sequentialIndex) {
      const totalPoints = validPoints.length;
      let count = 0;
      for (let i = 0; i <= sequentialIndex; i += 1) {
        const point = validPoints[i];
        const sidebarIndex = this.getWaypointSidebarIndex(point, i);
        const role = this.getWaypointRole(
          point,
          sidebarIndex,
          i,
          totalPoints,
        );
        if (role !== 'start') {
          count += 1;
        }
      }
      return count;
    },
    getPlacePoint(place) {
      const loc = place && place.originalLocation;
      if (!loc) {
        return null;
      }
      const lat = Number(loc.lat);
      const lng = Number(loc.lng);
      if (isNaN(lat) || isNaN(lng)) {
        return null;
      }
      return { lat, lng };
    },
    getPolylineEndpoint(polyline, which) {
      if (!polyline) {
        return null;
      }
      try {
        const linestring = H.geo.LineString.fromFlexiblePolyline(polyline);
        const coords = linestring.getLatLngAltArray();
        if (!coords || coords.length < 2) {
          return null;
        }
        if (which === 'start') {
          return { lat: coords[0], lng: coords[1] };
        }
        return {
          lat: coords[coords.length - 3],
          lng: coords[coords.length - 2],
        };
      } catch (e) {
        return null;
      }
    },
    buildViewRouteMarkerPoints(sections) {
      if (!sections || !sections.length) {
        return [];
      }

      const points = [];
      const firstSection = sections[0];
      const startPoint =
        this.getPolylineEndpoint(firstSection.polyline, 'start') ||
        this.getPlacePoint(firstSection.departure && firstSection.departure.place);
      if (this.isValidMarkerPoint(startPoint)) {
        points.push(startPoint);
      }

      for (let i = 1; i < sections.length; i++) {
        const section = sections[i];
        const intermediatePoint =
          this.getPolylineEndpoint(section.polyline, 'start') ||
          this.getPlacePoint(section.departure && section.departure.place);
        if (this.isValidMarkerPoint(intermediatePoint)) {
          points.push(intermediatePoint);
        }
      }

      const lastSection = sections[sections.length - 1];
      const endPoint =
        this.getPolylineEndpoint(lastSection.polyline, 'end') ||
        this.getPlacePoint(lastSection.arrival && lastSection.arrival.place);
      if (this.isValidMarkerPoint(endPoint)) {
        const isDuplicate =
          points.length &&
          points[points.length - 1].lat === endPoint.lat &&
          points[points.length - 1].lng === endPoint.lng;
        if (!isDuplicate) {
          points.push(endPoint);
        }
      }

      return points;
    },
    createRouteMarkerIcon(svg) {
      return new H.map.Icon(
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
        { anchor: ROUTE_MARKER_ANCHOR },
      );
    },
    generateNumberedMarkerSVG(label) {
      return `${ROUTE_MARKER_SVG_OPEN}${ROUTE_MARKER_PIN_PATHS}<text x="32" y="29" text-anchor="middle" font-family="Arial Black, Helvetica, sans-serif" font-size="20" font-weight="600"> ${label} </text></svg>`;
    },
    generateMarkerSVG(validPoints, sequentialIndex, role = null) {
      const totalPoints = validPoints.length;
      const point = validPoints[sequentialIndex];
      const sidebarIndex = this.getWaypointSidebarIndex(
        point,
        sequentialIndex,
      );
      const resolvedRole =
        role ||
        this.getWaypointRole(
          point,
          sidebarIndex,
          sequentialIndex,
          totalPoints,
        );

      if (resolvedRole === 'start') {
        return ROUTE_START_MARKER_SVG;
      }
      const stopNumber = this.getNonStartStopNumber(
        validPoints,
        sequentialIndex,
      );
      return this.generateNumberedMarkerSVG(String(stopNumber));
    },
    generateEntryExitMarkerSVG(type = 'entry') {
      if (type === 'exit') {
        return `<svg data-v-23ff73ee="" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="yellow" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path data-v-23ff73ee="" d="M7 22V2l10 5-10 5"></path></svg>`;
      }
      return `<svg data-v-23ff73ee="" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#66cb9e" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path data-v-23ff73ee="" d="M7 22V2l10 5-10 5"></path></svg>`;
    },
    circleSvg() {
      return `<svg width="18px" height="18px" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="7.5" cy="7.5" r="7" fill="#498DE2" />
            <path
              d="M7.5 9.125C8.39746 9.125 9.125 8.39746 9.125 7.5C9.125 6.60254 8.39746 5.875 7.5 5.875C6.60254 5.875 5.875 6.60254 5.875 7.5C5.875 8.39746 6.60254 9.125 7.5 9.125ZM7.5 10.125C8.94975 10.125 10.125 8.94975 10.125 7.5C10.125 6.05025 8.94975 4.875 7.5 4.875C6.05025 4.875 4.875 6.05025 4.875 7.5C4.875 8.94975 6.05025 10.125 7.5 10.125Z"
              fill="white"
            />
          </svg>`;
    },
    takeFullSnapshot() {
      this.map.capture((canvas) => {
        this.map_image_data = canvas.toDataURL();
      });
    },
  },
  async mounted() {
    await this.initMap();
  },
  beforeDestroy() {
    this.closeExclusionBubble();
    if (this.map && this.planContextMenuHandler) {
      const viewport = this.map.getViewPort();
      viewport.element.removeEventListener(
        'contextmenu',
        this.planContextMenuHandler,
      );
    }
    this.clearPlanDraftPin();
  },
  computed: {
    alphabet() {
      return [
        '📍',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
        '24',
        '25',
        '26',
        '27',
        '28',
        '29',
        '30',
        '31',
        '32',
        '33',
        '34',
        '35',
        '36',
        '37',
        '38',
        '39',
        '40',
        '41',
        '42',
        '43',
        '44',
        '45',
        '46',
        '47',
        '48',
        '49',
        '50',
      ];
      // return "abcdefghijklmnopqrstuvwxyz".toUpperCase().split("")
    },
  },
  watch: {
    selectedVersionIndex: {
      async handler(val) {
        if (!this.routeVersionMode || !this.routeData?.routes?.length) {
          return;
        }
        await this.displayRouteVersions(val || 0);
      },
    },
    selected_route_id: {
      async handler(val) {
        if (val) {
          await this.chooseRoute(val);
        }
      },
      deep: true,
    },
    reset_all: {
      async handler(val) {
        if (val) {
          await this.clearMapRouteObjects();
          this.generated_route = false;
        }
      },
      deep: true,
    },
    is_edit: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.setMarkersDraggableForView();
          });
        }
      },
      deep: true,
    },
    stored_tmp_marker: {
      handler(val) {
        if (val?.length) {
          this.tmp_marker = val;
        }
      },
      deep: true,
    },
    start_printing: {
      async handler(val) {
        if (val) {
          await this.takeFullSnapshot();
        }
      },
      deep: true,
    },
    routeData: {
      async handler() {
        if (this.routeVersionMode) {
          await this.displayRouteVersions(this.selectedVersionIndex || 0);
          return;
        }
        await this.displayAlternativeRoutes();
      },
      deep: true,
    },
    exclusionOverlays: {
      handler(val) {
        this.renderExclusionOverlays(val);
      },
      deep: true,
    },
    planMapPickEnabled() {
      if (this.is_plan && this.map) {
        this.bindPlanContextMenuIfEnabled();
      }
    },
    selected_points: {
      deep: true,
      async handler(val) {
        if (!val.length) return;
        // During route edit, waypoint moves are handled on-map; avoid wiping route/passthroughs.
        if (this.is_edit && this.routeData?.routes?.length) {
          const prevLen = this.cloned_points?.length ?? 0;
          if (val.length !== prevLen) {
            await this.syncWaypointMarkersFromPoints(val);
          }
          this.cloned_points = await structuredClone(val);
          return;
        }
        let changed_point_index = val.length - 1;
        if (this.cloned_points) {
          let stop = false;
          this.cloned_points.forEach((point, index) => {
            if (!stop) {
              if (
                point.lat !== val[index].lat ||
                point.lng !== val[index].lng
              ) {
                changed_point_index = index;
                stop = true;
              }
            }
          });
        }
        await this.renderAddedPoints(val, changed_point_index);
        this.cloned_points = await structuredClone(val);
      },
    },
  },
};
</script>
<style lang="scss">
.exclusion-bubble {
  max-width: 250px;
  min-width: 160px;
  padding: 0;
  box-sizing: border-box;
  line-height: 1.35;
  font-size: 13px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.exclusion-bubble-title {
  padding: 8px 28px 6px 10px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exclusion-bubble-divider {
  border: 0;
  border-top: 1px solid #d0d5da;
  margin: 0;
}

.exclusion-bubble-conditions {
  padding: 6px 10px;
}

.exclusion-bubble-condition {
  line-height: 1.4;
}

.exclusion-bubble-source {
  padding: 6px 10px 8px;
  font-size: 12px;
  color: #F2374D;
}

.H_ib_body:has(.exclusion-bubble) {
  min-width: 160px;
}
.zoom-wrap {
  top: 120px;
  right: 50px;
  z-index: 100;
}
</style>
